import React, { useRef } from 'react';
import 'twin.macro';
import { ROSelect } from 'containers/Eleves/List/Table/Cells/components/ROSelect';
import { Tag } from 'components/Tag';
import { isDefined } from 'types/utils';
import { autoPlacement, autoUpdate, useFloating } from '@floating-ui/react-dom';
import { inner } from '@floating-ui/react';
import PortalWrapper from 'components/PortalWrapper';
import tw from 'twin.macro';
import { EleveGroupSelector } from 'components/EleveGroupSelector';
import { EleveGroup } from 'types/eleveGroups';

interface Props {
  eleveGroups: EleveGroup[];
  value: string[];
  onChange: (v: EleveGroup[]) => void;
  readonly?: boolean;
}

export function Groups(props: Props) {
  const { onChange, value, readonly, eleveGroups } = props;
  const [groupIds, setGroupIds] = React.useState<string[]>(value);
  const [editing, setEditing] = React.useState<boolean>(false);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const selectedGroups = groupIds
    .map(id => eleveGroups.find(g => g.id === id))
    .filter(isDefined);

  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        crossAxis: true,
        alignment: 'start',
        autoAlignment: false,
        allowedPlacements: ['top-start', 'right-end', 'bottom-end', 'left-end'],
      }),
      inner({
        listRef,
        index: 0,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const opts = selectedGroups.map(g => ({
    id: g.id,
    value: g.name,
    color: g.color,
  }));

  if (readonly) {
    if (opts.length < 1) return null;

    return (
      <div tw="flex flex-wrap gap-x-1">
        {opts.map(opt => (
          <Tag key={opt.id} color={opt?.color || 'gris-moderate'}>
            {opt?.value}
          </Tag>
        ))}
      </div>
    );
  }

  return (
    <div
      tw="max-w-[18rem]"
      ref={refs.setReference}
      data-testid="eleve-groups-wrapper"
    >
      <ROSelect onClick={() => setEditing(true)} data-testid="groups">
        <div
          css={[tw`flex flex-wrap gap-x-1 gap-y-1`, editing && tw`invisible`]}
        >
          {opts.map(opt => (
            <Tag key={opt.id} color={opt?.color || 'gris-moderate'}>
              <span tw="truncate">{opt?.value}</span>
            </Tag>
          ))}
        </div>
      </ROSelect>
      {editing && (
        <PortalWrapper portal>
          <div ref={refs.setFloating} style={floatingStyles}>
            <div
              ref={node => {
                listRef.current[0] = node;
              }}
            >
              <EleveGroupSelector
                tw="border-opacity-0 hover:border-opacity-100 px-1 max-w-[18rem]"
                value={selectedGroups}
                onClickOutside={() => {
                  setTimeout(() => setEditing(false), 100);
                }}
                onChange={v => {
                  onChange(v);
                  setGroupIds(v.map(g => g.id));
                }}
                className="w-full"
                openOnMount
              />
            </div>
          </div>
        </PortalWrapper>
      )}
    </div>
  );
}
